<template>
  <div
    data-testid="TimezoneDropdown"
    :class="{
      'relative w-full md:max-w-195px': true,
    }"
  >
    <div
      :class="{
        'flex flex-col gap-6px': true,
      }"
    >
      <span
        :class="{
          'text-11px font-extrabold uppercase tracking-[1.54px] text-www-color-accent': true,
        }"
      >
        Timezone
      </span>
      <button
        type="button"
        :class="{
          'bg-white flex min-h-11 w-full items-center justify-between gap-2.5 rounded-[10px] border border-solid border-www-color-accent-soft px-3 py-2.5 text-www-color-neutral-dark transition-[border-color,box-shadow] duration-200': true,
          'focus-visible:border-www-color-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(64,170,29,0.16)]': true,
        }"
        @click="open = !open"
      >
        <span
          :class="{
            'min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-sm font-bold text-www-color-neutral-dark': true,
            'text-www-color-neutral-medium': !props.modelValue,
          }"
        >
          {{ props.modelValue || 'Select timezone' }}
        </span>
        <span
          :class="{
            'h-5 w-5 text-www-color-accent transition-transform duration-200': true,
            'rotate-180': open,
          }"
        >
          <svg
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              stroke-width="1.75"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
      </button>
    </div>

    <div
      v-if="open"
      :class="{
        'absolute left-0 top-[calc(100%+6px)] z-30 w-full rounded-xl border border-solid border-www-color-accent-soft bg-www-color-accent-almost-white shadow-[0_12px_34px_rgba(13,46,72,0.16)]': true,
      }"
    >
      <div
        :class="{
          'border-b border-solid border-www-color-accent-soft p-2.5': true,
        }"
      >
        <input
          v-model="search"
          type="search"
          placeholder="Search timezone..."
          :class="{
            'bg-white min-h-9 w-full rounded-lg border border-solid border-www-color-accent-soft px-2.5 py-2 text-sm font-semibold text-www-color-neutral-dark placeholder:text-www-color-neutral-medium': true,
            'focus-visible:border-www-color-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(64,170,29,0.16)]': true,
          }"
        />
      </div>

      <ul
        :class="{
          'm-0 max-h-[260px] list-none overflow-y-auto p-1.5': true,
        }"
      >
        <li
          v-for="timezone in filteredTimezones"
          :key="timezone"
        >
          <button
            type="button"
            :class="{
              'bg-transparent w-full rounded-lg border-0 px-2.5 py-2 text-left text-sm font-semibold text-www-color-neutral-dark transition-colors duration-150': true,
              'hover:bg-www-color-accent-soft hover:text-www-color-accent-light focus-visible:bg-www-color-accent-soft focus-visible:text-www-color-accent-light focus-visible:outline-none': true,
              'bg-www-color-accent-soft text-www-color-accent-light': timezone === props.modelValue,
            }"
            @click="selectTimezone(timezone)"
          >
            {{ timezone }}
          </button>
        </li>

        <li
          v-if="filteredTimezones.length === 0"
          :class="{
            'px-2.5 py-2.5 text-13px font-semibold text-www-color-neutral-medium': true,
          }"
        >
          No timezones found
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
const props = defineProps<{
  modelValue: string;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const open = ref<boolean>(false);
const search = ref<string>('');

const fallbackTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const supportedTimeZones = computed<string[]>((): string[] => {
  const intlObject = Intl as unknown as {
    supportedValuesOf?: (key: 'timeZone') => string[];
  };

  if (typeof intlObject.supportedValuesOf !== 'function') {
    return [fallbackTimeZone];
  }

  return intlObject.supportedValuesOf('timeZone');
});

const filteredTimezones = computed<string[]>((): string[] => {
  const query = search.value.toLowerCase().trim();

  if (!query) {
    return supportedTimeZones.value;
  }

  return supportedTimeZones.value.filter((timezone): boolean =>
    timezone.toLowerCase().includes(query)
  );
});

const selectTimezone = (timezone: string): void => {
  emit('update:modelValue', timezone);
  open.value = false;
  search.value = '';
};
</script>
